import { useContext, useEffect, useState } from 'react';

import { useSelector } from 'react-redux';

import { CurrentDataContext } from 'src/app/current-data';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { StatsigFeatureKeys } from 'src/features';
import { useFlag } from 'src/hooks/flag';
import { NON_DISMISSIBLE_UNLINKED_WORKSPACE_FLAG_ID } from 'src/sections/global/components/flags/non-dismissible-unlinked-workspace-flag';
import { getisUnlinkedWsFlagNotificationRelevant } from 'src/selectors/global-selectors';
import { publishScreenEvent } from 'src/utils/analytics/publish';
import { captureExceptionWithTags } from 'src/utils/sentry';
import { atlassianStore as store } from 'src/utils/store';

import UnlinkedWorkspaceModal from './modals/unlinked-workspace-modal';

const MODAL_RECURRENCE_TIME_MS = 1000 * 60 * 60 * 48;
export const UNLINKED_WORKSPACE_NOTICE_STORAGE_KEY =
  'bbc-unlinked-workspace-flag-dismissed'; // Same key as used in the flag component but we use a different entry in the JSON here (modal_dismissed_at) to track modal dismissals separately.

export const checkIfWorkspaceLinked = async (workspaceSlug?: string) => {
  try {
    if (!workspaceSlug) {
      return null;
    }

    const response = await fetch(
      `/!api/internal/workspaces/${workspaceSlug}/org-link-status`
    );
    if (!response.ok) {
      throw new Error(
        `Failed to fetch workspace link status: ${response.statusText}`
      );
    }

    const json = await response.json();
    return json?.has_catalogue_record;
  } catch (error) {
    captureExceptionWithTags(error, { workspaceSlug: workspaceSlug || '' });
    return null;
  }
};

export const checkIfDismissedRecently = () => {
  const dismissedData = store.get(UNLINKED_WORKSPACE_NOTICE_STORAGE_KEY);

  if (dismissedData && dismissedData.modal_dismissed_at) {
    try {
      const dismissedAt: Date = new Date(dismissedData.modal_dismissed_at);
      const now = new Date().getTime();
      const timeSinceDismissed = now - dismissedAt.getTime();

      if (timeSinceDismissed < MODAL_RECURRENCE_TIME_MS) {
        return true;
      }
    } catch (error) {
      captureExceptionWithTags(error, { dismissedData });
      return true;
    }
  }

  return false;
};

export const checkIfInitWriteWorked = (localStorageData: any) => {
  const FIVE_SECONDS = 5000;

  if (localStorageData?.initialized_at) {
    try {
      const initializedAtDate = new Date(localStorageData?.initialized_at);

      const now = new Date();
      const timeSinceInitialized = now.getTime() - initializedAtDate.getTime();

      if (timeSinceInitialized < FIVE_SECONDS) {
        return true;
      }
    } catch (error) {
      captureExceptionWithTags(error, { localStorageData });
      return false;
    }
  }

  return false;
};

export const UnlinkedWorkspaceNotificationsWrapper = () => {
  const [shouldShowNotification, setShouldShowNotification] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { showFlagComponent, dismissFlag } = useFlag();

  const modalGateIsEnabled = useStatsigGate(
    StatsigFeatureKeys.bbcUnlinkedWsModal
  );
  const flagGateIsEnabled = useStatsigGate(
    StatsigFeatureKeys.bbcNonDismissibleUnlinkedWsFlag
  );

  const { workspace: currentWorkspace } = useContext(CurrentDataContext);
  const isNotificationRelevant = useSelector(
    getisUnlinkedWsFlagNotificationRelevant
  );
  const wasDismissedRecently = checkIfDismissedRecently();

  useEffect(() => {
    if (shouldShowNotification) {
      return;
    }

    if (!isNotificationRelevant) {
      setShouldShowNotification(false);
      return;
    }

    const checkStatusAndShowOrHideNotification = async () => {
      const isWorkspaceLinked = await checkIfWorkspaceLinked(
        currentWorkspace?.slug
      );

      if (isWorkspaceLinked === false) {
        setShouldShowNotification(true);
      }
    };

    checkStatusAndShowOrHideNotification();

    const existingData = store.get(UNLINKED_WORKSPACE_NOTICE_STORAGE_KEY);
    store.set(UNLINKED_WORKSPACE_NOTICE_STORAGE_KEY, {
      ...existingData,
      initialized_at: new Date().toISOString(),
    });
  }, [
    currentWorkspace,
    isNotificationRelevant,
    wasDismissedRecently,
    setShouldShowNotification,
    shouldShowNotification,
  ]);

  useEffect(() => {
    if (!shouldShowNotification) {
      return;
    }

    const localStorageData = store.get(UNLINKED_WORKSPACE_NOTICE_STORAGE_KEY);
    const succeededWritingToLocalStorage =
      checkIfInitWriteWorked(localStorageData);

    if (wasDismissedRecently || !succeededWritingToLocalStorage) {
      if (flagGateIsEnabled) {
        showFlagComponent(NON_DISMISSIBLE_UNLINKED_WORKSPACE_FLAG_ID);
        publishScreenEvent('unlinked-workspace-flag-shown');
      }

      setShowModal(false);

      if (!succeededWritingToLocalStorage) {
        publishScreenEvent('unlinked-workspace-modal-initialization-failed');
      }
    } else {
      if (modalGateIsEnabled) {
        setShowModal(true);
        publishScreenEvent('unlinked-workspace-modal-shown');
      }

      dismissFlag(NON_DISMISSIBLE_UNLINKED_WORKSPACE_FLAG_ID);
    }
  }, [
    setShowModal,
    modalGateIsEnabled,
    shouldShowNotification,
    wasDismissedRecently,
    showFlagComponent,
    dismissFlag,
    flagGateIsEnabled,
  ]);

  if (!showModal) {
    return null;
  }

  const onClose = async () => {
    const dismissedData = store.get(UNLINKED_WORKSPACE_NOTICE_STORAGE_KEY);
    await store.set(UNLINKED_WORKSPACE_NOTICE_STORAGE_KEY, {
      ...dismissedData,
      modal_dismissed_at: new Date().toISOString(),
    });

    setShowModal(false);
    setShouldShowNotification(false); // To re-check and show the flag if still relevant
  };

  return <UnlinkedWorkspaceModal onClose={onClose} />;
};

const UnlinkedWorkspaceNotifications = () => {
  const gateIsEnabled = useStatsigGate(
    StatsigFeatureKeys.bbcUnlinkedWsNotifications
  );
  const pageBlockIsEnabled = useStatsigGate(
    StatsigFeatureKeys.bbcUnlinkedWsPageBlock
  );

  if (pageBlockIsEnabled) {
    return null;
  }

  if (!gateIsEnabled) {
    return null;
  }

  return <UnlinkedWorkspaceNotificationsWrapper />;
};

// eslint-disable-next-line import/no-default-export
export default UnlinkedWorkspaceNotifications;
